import { ActionIcon, Button, Indicator, Popover, TextInput, Tooltip, type MantineSize } from '@mantine/core';
import { Breakpoints } from '@vision/ui/constants';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { If } from '@vision/ui/components';
import { useClickOutside, useDebouncedValue, useDidUpdate, useInputState, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import classes from './FilterSearch.module.css';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

interface FilterSearchProps {
  inputWidth?: number | string;
  onSearch: (value: string) => void;
  searchText: string;
  size?: MantineSize;
  tooltipLabel?: string;
  tooltipDisabled?: boolean;
}

const BUTTON_SIZE_MAP: Record<string, number> = {
  lg: 50,
  md: 42,
  sm: 36,
  xl: 60,
  xs: 30,
};

function _FilterSearch({ inputWidth, onSearch, searchText, size, tooltipLabel, tooltipDisabled }: FilterSearchProps) {
  const isInputInsidePopover = useMediaQuery(Breakpoints.DOWN.MD);
  const [opened, setOpened] = useState(false);
  const [value, setValue] = useInputState(searchText);
  const { t } = useTranslation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonSize = size ? BUTTON_SIZE_MAP[size] : BUTTON_SIZE_MAP.sm;

  const handleClickOutside = () => {
    if (value) {
      return;
    }
    setOpened(false);
  };

  const [debouncedValue] = useDebouncedValue(value, 300);
  useClickOutside(handleClickOutside, null, [btnRef.current, inputRef.current]);

  const clearSearch = () => {
    setValue('');
    setOpened(false);
  };

  const handleOpen = () => {
    setOpened(true);
  };

  const handleToggle = () => {
    setOpened((prev) => !prev);
  };

  useDidUpdate(() => {
    onSearch(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    if (isInputInsidePopover !== undefined) {
      setOpened(!!searchText && !isInputInsidePopover);
    }
  }, [isInputInsidePopover]);

  return (
    <div
      className={clsx('filter-search', {
        'filter-search--opened': opened,
      })}
    >
      <Popover
        opened={opened}
        disabled={!isInputInsidePopover}
        withArrow={false}
        offset={{
          crossAxis: -42,
          mainAxis: 5,
        }}
      >
        <Popover.Target>
          <Indicator disabled={!isInputInsidePopover || !value} size={8}>
            <Button
              ref={btnRef}
              className={clsx(classes.filterSearchBtn, {
                [classes.filterSearchBtnHidden]: opened && !isInputInsidePopover,
              })}
              variant="outline"
              color="gray"
              onClick={() => {
                if (isInputInsidePopover) {
                  handleToggle();
                } else {
                  handleOpen();
                }
              }}
              size={size}
              w={buttonSize}
              h={buttonSize}
              p={0}
              data-testid="button-filter-search"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </Button>
          </Indicator>
        </Popover.Target>
        <Popover.Dropdown className="popover-dropdown-unstyled">
          <TextInput
            autoFocus={true}
            data-testid="input-filter-search"
            placeholder={t('search')}
            ref={inputRef}
            size={size}
            value={value}
            w={inputWidth}
            onChange={setValue}
            rightSection={
              <ActionIcon size="xs" onClick={clearSearch} color="gray">
                <FontAwesomeIcon icon={faXmark} />
              </ActionIcon>
            }
          />
        </Popover.Dropdown>
      </Popover>

      <If value={!isInputInsidePopover && opened}>
        <Tooltip label={tooltipLabel} disabled={tooltipDisabled || !tooltipLabel} position="bottom">
          <TextInput
            autoFocus={true}
            data-testid="input-filter-search"
            placeholder={t('search')}
            ref={inputRef}
            value={value}
            w={inputWidth}
            onChange={setValue}
            size={size || 'sm'}
            rightSection={
              <ActionIcon size="xs" onClick={clearSearch} color="gray">
                <FontAwesomeIcon icon={faXmark} />
              </ActionIcon>
            }
          />
        </Tooltip>
      </If>
    </div>
  );
}

export const FilterSearch = React.memo(_FilterSearch);
